import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormulaTemplateOption } from "./useFormulaTemplates";

export type DropdownSelection =
  | { kind: "template"; id: string }
  | { kind: "custom" };

interface UseTemplateSelectionArgs {
  sourceTemplateId: string | null;
  templates: FormulaTemplateOption[];
  isLoadingTemplates: boolean;
  savedExpression: string;
  onLoadDefaultExpression: (expression: string) => void;
}

const useTemplateSelection = ({
  sourceTemplateId,
  templates,
  isLoadingTemplates,
  savedExpression,
  onLoadDefaultExpression,
}: UseTemplateSelectionArgs) => {
  const [userSelection, setUserSelection] = useState<DropdownSelection | null>(
    null,
  );

  const initialSelection = useMemo<DropdownSelection>(() => {
    const initialTemplate =
      sourceTemplateId !== null
        ? templates.find((t) => t.id === sourceTemplateId)
        : undefined;
    if (initialTemplate) return { kind: "template", id: initialTemplate.id };

    const savedExpressionIsEmpty = savedExpression.trim() === "";
    if (savedExpressionIsEmpty && templates.length > 0)
      return { kind: "template", id: templates[0].id };

    return { kind: "custom" };
  }, [sourceTemplateId, templates, savedExpression]);

  const selection = userSelection ?? initialSelection;

  const isPending =
    userSelection === null &&
    isLoadingTemplates &&
    initialSelection.kind !== "template";

  const hasLoadedDefaultExpression = useRef(false);
  useEffect(() => {
    if (hasLoadedDefaultExpression.current) return;

    const savedExpressionIsEmpty = savedExpression.trim() === "";
    const shouldSeedDefaultTemplate =
      userSelection === null && selection.kind === "template";
    if (!savedExpressionIsEmpty || !shouldSeedDefaultTemplate) return;

    const defaultTemplate = templates.find((t) => t.id === selection.id);
    if (!defaultTemplate) return;

    hasLoadedDefaultExpression.current = true;
    onLoadDefaultExpression(defaultTemplate.expression);
  }, [
    userSelection,
    savedExpression,
    selection,
    templates,
    onLoadDefaultExpression,
  ]);

  const selectTemplate = useCallback(
    (templateId: string) =>
      setUserSelection({ kind: "template", id: templateId }),
    [],
  );

  const selectCustom = useCallback(
    () => setUserSelection({ kind: "custom" }),
    [],
  );

  const markCustomized = useCallback(
    () =>
      setUserSelection((prev) =>
        prev?.kind === "custom" ? prev : { kind: "custom" },
      ),
    [],
  );

  return {
    selection,
    isSelectionPending: isPending,
    selectTemplate,
    selectCustom,
    markCustomized,
  };
};

export default useTemplateSelection;
